// ============================================
// Manor Miser - UI Button Component
// ============================================
// 9-slice pixel art button with bitmap font label.

import Phaser from 'phaser';

export class UIButton extends Phaser.GameObjects.Container {
  private bg: Phaser.GameObjects.NineSlice;
  private label: Phaser.GameObjects.BitmapText;
  private isDisabled: boolean = false;
  private isPressed: boolean = false;
  private btnWidth: number;
  private btnHeight: number;
  private tintColor: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    text: string,
    private onClick: () => void,
    width: number = 150,
    height: number = 45,
    tint: number = 0xd4a855,
    inset: number = 5,
  ) {
    super(scene, x, y);

    this.btnWidth = width;
    this.btnHeight = height;
    this.tintColor = tint;

    // 9-slice background
    this.bg = scene.add.nineslice(0, 0, 'btn-unpressed', 0, width, height, inset, inset, inset, inset);
    this.bg.setTint(tint);
    this.add(this.bg);

    // Bitmap text label — centered on the button
    this.label = scene.add.bitmapText(0, 0, 'pixel', text.toUpperCase());
    this.label.setOrigin(0.5);
    this.label.setTint(0xf5e6d3);
    this.fitLabel();
    this.add(this.label);

    // Interactive hit area
    this.setSize(width, height);
    this.setInteractive({ useHandCursor: true });

    // Pre-compute a brighter hover tint
    const r = Math.min(255, ((tint >> 16) & 0xFF) + 30);
    const g = Math.min(255, ((tint >> 8) & 0xFF) + 30);
    const b = Math.min(255, (tint & 0xFF) + 30);
    const hoverTint = (r << 16) | (g << 8) | b;

    this.on('pointerover', () => {
      if (!this.isDisabled) {
        this.bg.setTint(hoverTint);
      }
    });

    this.on('pointerout', () => {
      this.isPressed = false;
      if (!this.isDisabled) {
        this.bg.setTexture('btn-unpressed');
        this.bg.setTint(tint);
        this.label.setY(0);
      }
    });

    this.on('pointerdown', () => {
      if (!this.isDisabled) {
        this.isPressed = true;
        this.bg.setTexture('btn-pressed');
        this.label.setY(1);
      }
    });

    this.on('pointerup', () => {
      if (!this.isDisabled && this.isPressed) {
        this.isPressed = false;
        this.bg.setTexture('btn-unpressed');
        this.label.setY(0);
        this.onClick();
      }
    });

    scene.add.existing(this);
  }

  private fitLabel(): void {
    this.label.setScale(1);
    const maxWidth = this.btnWidth - 6;
    if (this.label.width > maxWidth) {
      this.label.setScale(maxWidth / this.label.width);
    }
  }

  setDisabled(disabled: boolean): void {
    this.isDisabled = disabled;
    if (disabled) {
      this.bg.setTint(0x555555);
      this.label.setTint(0x888888);
      this.disableInteractive();
    } else {
      this.bg.setTint(this.tintColor);
      this.label.setTint(0xf5e6d3);
      this.setInteractive({ useHandCursor: true });
    }
  }

  setText(text: string): void {
    this.label.setText(text.toUpperCase());
    this.fitLabel();
  }

  pulse(): void {
    this.scene.tweens.add({
      targets: this,
      scale: 1.1,
      duration: 200,
      yoyo: true,
      repeat: 2,
    });
  }
}
